// New Trainee in the Training Portal. Route: #/training/{site}/new.
// Words: NEW_TRAINEE_WORDS.md. Mockup: MOCKUP_NEW_TRAINEE_APPROVED.html (2026-10-06).
// Two ways to pick the tag: Scan Tag (camera) or the next free tag. No typed IDs.
// The component owns its own box and its own events. It uses data-nt attributes,
// never data-act and never <form>, so the portal's handlers leave it alone.

import { steps } from "./step-back";
import "./new-trainee.css";
import { DEPARTMENTS, EXPERIENCE, ROLES, lotoCheck, lotoLine, traineeInputProblem, type ClaimInput } from "./claim";
import { callServer } from "./firebase";
import { addedLine, shortSiteName, type TraineeTagCheck } from "./new-trainee";
import { readQrLink } from "./qr-link";
import { startQrScanner, type QrScanner } from "./qr-scanner";
import { dateInSiteTime } from "./training-server";

type AddResult =
  | { kind: "added"; tagId: string; holderName: string; lotoReported: boolean }
  | { kind: "refused"; message: string };

export interface NewTraineeOptions {
  siteNumber: string;
  siteName: string;
  /** Called after the trainee is saved, with the green line for the node view. */
  onAdded: (tagId: string, line: string) => Promise<void> | void;
}

const ROLE_LABELS: Record<string, string> = { "General Maintenance": "General Maint.", "Facilities Maintenance": "Facilities Maint." };
const YEARS_LABELS: Record<string, string> = {
  "Less than 1 year": "<1",
  "1–3 years": "1–3",
  "4–7 years": "4–7",
  "8–15 years": "8–15",
  "15+ years": "15+",
};

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

type View =
  | { kind: "pick" }
  | { kind: "scan"; note: string }
  | { kind: "checking"; text: string }
  | { kind: "form"; tagId: string; sessionCode: string | null }
  | { kind: "refused"; message: string };

export function mountNewTrainee(host: HTMLElement, opts: NewTraineeOptions): void {
  const site = opts.siteNumber;
  const shortName = shortSiteName(opts.siteName, site);
  const back = `#/training/${site}`;
  let view: View = { kind: "pick" };
  /** The next free tag, looked up when the screen opens. */
  let next: TraineeTagCheck | null = null;
  let nextError = "";
  let scanner: QrScanner | null = null;
  let busy = false;
  let saveError = "";
  const D = { first: "", last: "", dept: "", role: "", years: "", lotoDate: "", lotoSkipped: false, phone: "", email: "" };

  const today = () => dateInSiteTime(new Date());

  function input(tagId: string): ClaimInput {
    return {
      tagId,
      sessionCode: "",
      firstName: D.first.trim(),
      lastName: D.last.trim(),
      department: D.dept,
      role: D.role,
      yearsExperience: D.years,
      lotoDate: D.lotoSkipped ? "" : D.lotoDate,
      lotoSkipped: D.lotoSkipped,
      phone: D.phone.trim(),
      email: D.email.trim().toLowerCase(),
    };
  }

  // ---- Screens ----

  function head(sub: string): string {
    return `<h2 class="nt-h">New Trainee</h2><div class="nt-sub">${esc(sub)}</div>`;
  }

  function nextOption(): string {
    if (nextError) {
      return `<button class="nt-opt" type="button" data-nt="next-retry"><span class="ic">+</span><span><b>Next free tag</b><small>${esc(nextError)} Tap to try again.</small></span></button>`;
    }
    if (!next) {
      return `<button class="nt-opt" type="button" disabled><span class="ic">+</span><span><b>Next free tag</b><small>Finding the next free tag…</small></span></button>`;
    }
    if (next.kind === "ok") {
      return `<button class="nt-opt" type="button" data-nt="next"><span class="ic">+</span><span><b>Next free tag · ${esc(next.tagId)}</b><small>For a trainee with no tag yet. Hand them ${esc(next.tagId)}.</small></span></button>`;
    }
    return `<button class="nt-opt" type="button" disabled><span class="ic">+</span><span><b>Next free tag</b><small>${esc(next.message)}</small></span></button>`;
  }

  function pickHtml(): string {
    return `${head(`${opts.siteName || shortName} · pick the bag tag`)}
      <button class="nt-opt" type="button" data-nt="scan"><span class="ic">▣</span><span><b>Scan Tag</b><small>Point the camera at the bag tag's QR.</small></span></button>
      ${nextOption()}
      <a class="nt-btn line" href="${back}">Cancel</a>`;
  }

  function scanHtml(note: string): string {
    return `<h2 class="nt-h">Scan Tag</h2><div class="nt-sub">Hold the bag tag inside the gold square.</div>
      <div class="nt-cam"><video id="nt-video" playsinline muted></video><div class="fr"></div><div class="tx" id="nt-cam-tx">Starting the camera…</div></div>
      <div class="note${note ? " red" : ""}" id="nt-scan-note">${esc(note || "Tag won't scan? Use the next free tag instead.")}</div>
      <button class="nt-btn line" type="button" data-nt="next">Use Next Free Tag</button>
      <button class="nt-btn line" type="button" data-nt="pick">Back</button>`;
  }

  function refusedHtml(message: string): string {
    return `${head(opts.siteName || shortName)}
      <div class="note red">${esc(message)}</div>
      <button class="nt-btn dark" type="button" data-nt="scan">Scan Another Tag</button>
      <button class="nt-btn line" type="button" data-nt="next">Use Next Free Tag</button>
      <a class="nt-btn line" href="${back}">Cancel</a>`;
  }

  function chips(group: string, values: readonly string[], labels: Record<string, string>, on: string): string {
    return `<div class="nt-chips">${values
      .map((v) => `<button type="button" class="nt-chip${v === on ? " on" : ""}" data-nt-chip="${group}" data-v="${esc(v)}">${esc(labels[v] ?? v)}</button>`)
      .join("")}</div>`;
  }

  function formHtml(tagId: string, sessionCode: string | null): string {
    const sessionText = sessionCode ? `Session ${sessionCode} is open. It is stamped on the record.` : "No session open. That is fine; you are signed in.";
    return `<div class="nt-tag"><div class="e">Bag tag · Unclaimed · ${esc(opts.siteName || shortName)}</div><div class="id">${esc(tagId)}</div><div class="s">Match this to the tag in hand</div></div>
      <div class="nt-two">
        <label class="nt-f"><span class="nt-lbl">First name</span><input class="nt-in" data-nt-in="first" autocomplete="off" value="${esc(D.first)}"></label>
        <label class="nt-f"><span class="nt-lbl">Last name</span><input class="nt-in" data-nt-in="last" autocomplete="off" value="${esc(D.last)}"></label>
      </div>
      <div class="nt-f"><span class="nt-lbl">Department</span>${chips("dept", DEPARTMENTS, {}, D.dept)}</div>
      <div class="nt-f"><span class="nt-lbl">Role</span>${chips("role", ROLES, ROLE_LABELS, D.role)}</div>
      <div class="nt-f"><span class="nt-lbl">Electrical experience (years)</span>${chips("years", EXPERIENCE, YEARS_LABELS, D.years)}</div>
      <label class="nt-f"><span class="nt-lbl">LOTO training date</span><input class="nt-in" type="date" data-nt-in="lotoDate" max="${today()}" value="${esc(D.lotoDate)}"${D.lotoSkipped ? " disabled" : ""}></label>
      <div id="nt-loto-line"></div>
      <label class="nt-skip"><input type="checkbox" data-nt-in="lotoSkipped"${D.lotoSkipped ? " checked" : ""}>No LOTO authorization</label>
      <div class="nt-two">
        <label class="nt-f"><span class="nt-lbl">Phone (optional)</span><input class="nt-in" type="tel" data-nt-in="phone" autocomplete="off" value="${esc(D.phone)}"></label>
        <label class="nt-f"><span class="nt-lbl">Email (optional)</span><input class="nt-in" type="email" data-nt-in="email" autocomplete="off" value="${esc(D.email)}"></label>
      </div>
      <div class="nt-sub" style="margin-top:0">${esc(sessionText)}</div>
      <div id="nt-save-err">${saveError ? `<div class="note red">${esc(saveError)}</div>` : ""}</div>
      <button class="nt-btn dark" type="button" data-nt="save" id="nt-save">Add Trainee</button>
      <button class="nt-btn line" type="button" data-nt="pick">Pick Another Tag</button>`;
  }

  function draw(): void {
    let inner: string;
    switch (view.kind) {
      case "pick":
        inner = pickHtml();
        break;
      case "scan":
        inner = scanHtml(view.note);
        break;
      case "checking":
        inner = `${head(opts.siteName || shortName)}<div class="nt-wait">${esc(view.text)}</div>`;
        break;
      case "form":
        inner = formHtml(view.tagId, view.sessionCode);
        break;
      default:
        inner = refusedHtml(view.message);
    }
    host.innerHTML = `<div class="nt">${inner}</div>`;
    if (view.kind === "form") refreshForm();
    if (view.kind === "scan") startCamera();
  }

  function go(v: View): void {
    if (v.kind !== "scan") stopCamera();
    view = v;
    // Past the tag pick is one Back step: the phone's Back gesture returns to the pick (approved 2026-10-09).
    steps().set(v.kind === "pick" ? 0 : 1, () => {
      if (view.kind !== "pick") go({ kind: "pick" });
    });
    draw();
  }

  /** LOTO line and the Add Trainee button, without redrawing the inputs. */
  function refreshForm(): void {
    if (view.kind !== "form") return;
    const box = host.querySelector("#nt-loto-line");
    const line = lotoLine(lotoCheck(D.lotoDate, D.lotoSkipped, today()));
    if (box) {
      const tone = line ? (line.tone === "ok" ? "green" : line.tone === "amber" ? "" : "red") : "";
      box.innerHTML = line ? `<div class="note ${tone}">${esc(line.text)}</div>` : "";
    }
    const btn = host.querySelector<HTMLButtonElement>("#nt-save");
    if (btn) {
      btn.disabled = busy || traineeInputProblem(input(view.tagId), today()) !== null;
      btn.textContent = busy ? "Adding…" : "Add Trainee";
    }
  }

  // ---- Server ----

  async function loadNext(): Promise<void> {
    next = null;
    nextError = "";
    try {
      next = await callServer<TraineeTagCheck>("checkNewTraineeTag", { siteNumber: site });
    } catch (e) {
      nextError = messageOf(e);
    }
    if (view.kind === "pick") draw();
  }

  async function useNext(): Promise<void> {
    go({ kind: "checking", text: "Finding the next free tag…" });
    await loadNext();
    if (next?.kind === "ok") go({ kind: "form", tagId: next.tagId, sessionCode: next.sessionCode });
    else go({ kind: "refused", message: next?.kind === "noneFree" || next?.kind === "refused" ? next.message : nextError || "Could not find a free tag." });
  }

  async function checkScanned(tagId: string): Promise<void> {
    go({ kind: "checking", text: `Checking ${tagId}…` });
    try {
      const res = await callServer<TraineeTagCheck>("checkNewTraineeTag", { siteNumber: site, tagId });
      if (res.kind === "ok") go({ kind: "form", tagId: res.tagId, sessionCode: res.sessionCode });
      else go({ kind: "refused", message: res.message });
    } catch (e) {
      go({ kind: "refused", message: `Could not check the tag. ${messageOf(e)}` });
    }
  }

  async function save(): Promise<void> {
    if (view.kind !== "form" || busy) return;
    const tagId = view.tagId;
    const data = input(tagId);
    const problem = traineeInputProblem(data, today());
    if (problem) {
      saveError = problem;
      draw();
      return;
    }
    busy = true;
    saveError = "";
    refreshForm();
    try {
      const res = await callServer<AddResult>("addTrainee", { siteNumber: site, ...data });
      if (res.kind === "refused") {
        busy = false;
        go({ kind: "refused", message: res.message });
        return;
      }
      await opts.onAdded(res.tagId, addedLine(res.holderName, res.tagId, res.lotoReported));
    } catch (e) {
      busy = false;
      saveError = `${messageOf(e)} Your entries are still here. Tap Add Trainee again.`;
      const box = host.querySelector("#nt-save-err");
      if (box) box.innerHTML = `<div class="note red">${esc(saveError)}</div>`;
      refreshForm();
    }
  }

  // ---- Camera ----

  function startCamera(): void {
    const video = host.querySelector<HTMLVideoElement>("#nt-video");
    const tx = host.querySelector<HTMLElement>("#nt-cam-tx");
    if (!video || !tx) return;
    let lastBad = "";
    let found: string | null = null;
    scanner = startQrScanner(
      video,
      (link) => {
        const read = readQrLink(link);
        if (read.kind === "badge" && /^\d{2}-QEW\d{2}$/.test(read.badgeId)) {
          // Hold a moment so the reader sees what was read (Robert, 2026-10-06).
          if (found) return false;
          found = read.badgeId;
          host.querySelector(".nt-cam")?.classList.add("found");
          tx.textContent = `Found ${read.badgeId} ✓`;
          window.setTimeout(() => {
            if (!scanner) return;
            stopCamera();
            void checkScanned(read.badgeId);
          }, 1000);
          return false;
        }
        const msg = read.kind === "asset" ? "That is an asset label. Scan the trainee's bag tag." : read.kind === "refused" ? read.message : "That is not a QEW bag tag. Scan the trainee's bag tag.";
        if (msg !== lastBad) {
          lastBad = msg;
          const note = host.querySelector("#nt-scan-note");
          if (note) {
            note.className = "note red";
            note.textContent = msg;
          }
        }
        return false;
      },
      (text) => {
        tx.textContent = text.startsWith("Looking") || text.startsWith("Starting") ? text : `${text} Use the next free tag.`;
      },
    );
  }

  function stopCamera(): void {
    scanner?.stop();
    scanner = null;
  }

  // ---- Events ----

  host.addEventListener("click", (ev) => {
    const el = (ev.target as HTMLElement).closest<HTMLElement>("[data-nt], [data-nt-chip]");
    if (!el || (el as HTMLButtonElement).disabled) return;
    const chip = el.dataset.ntChip as "dept" | "role" | "years" | undefined;
    if (chip) {
      D[chip] = el.dataset.v ?? "";
      el.parentElement?.querySelectorAll(".nt-chip").forEach((c) => c.classList.toggle("on", c === el));
      refreshForm();
      return;
    }
    switch (el.dataset.nt) {
      case "scan":
        go({ kind: "scan", note: "" });
        return;
      case "next":
        void useNext();
        return;
      case "next-retry":
        void loadNext();
        draw();
        return;
      case "pick":
        saveError = "";
        go({ kind: "pick" });
        if (!next || next.kind !== "ok") void loadNext();
        return;
      case "save":
        void save();
        return;
      default:
        return;
    }
  });

  function onField(el: HTMLInputElement): void {
    const key = el.dataset.ntIn;
    if (!key) return;
    if (key === "lotoSkipped") {
      D.lotoSkipped = el.checked;
      const date = host.querySelector<HTMLInputElement>('[data-nt-in="lotoDate"]');
      if (date) {
        date.disabled = el.checked;
        if (el.checked) {
          date.value = "";
          D.lotoDate = "";
        }
      }
    } else if (key === "first" || key === "last" || key === "lotoDate" || key === "phone" || key === "email") {
      D[key] = el.value;
    }
    refreshForm();
  }

  // Some browsers fire only change for checkboxes and date pickers, so both are heard.
  host.addEventListener("input", (ev) => onField(ev.target as HTMLInputElement));
  host.addEventListener("change", (ev) => onField(ev.target as HTMLInputElement));

  // Leaving the page stops the camera.
  window.addEventListener("hashchange", stopCamera, { once: true });

  draw();
  void loadNext();
}
